import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

const FREQ_LABELS = { monthly: 'Monthly', quarterly: 'Quarterly', annually: 'Annually' }
const FREQ_COLORS = { monthly: '#10B981', quarterly: '#3B82F6', annually: '#8B5CF6' }

function nextDate(freq) {
  const d = new Date()
  if (freq === 'monthly') d.setMonth(d.getMonth() + 1)
  if (freq === 'quarterly') d.setMonth(d.getMonth() + 3)
  if (freq === 'annually') d.setFullYear(d.getFullYear() + 1)
  return d.toISOString().split('T')[0]
}

export default function RecurringInvoicesPage() {
  const { profile } = useAuth()
  const [clients, setClients] = useState([])
  const [recurring, setRecurring] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [toast, setToast] = useState('')
  const [generating, setGenerating] = useState(null)

  const notify = msg => { setToast(msg); setTimeout(() => setToast(''), 3000) }

  const [form, setForm] = useState({
    client_id: '', frequency: 'monthly', next_invoice_date: nextDate('monthly'),
    line_items: [{ description: '', qty: 1, rate: '' }], tax_rate: 0, notes: '', active: true
  })

  useEffect(() => { load() }, [])

  async function load() {
    const { data: c } = await supabase.from('clients').select('id,full_name,business_name,email').eq('cpa_id', profile.id).order('business_name')
    setClients(c || [])
    const { data: r } = await supabase.from('recurring_invoices').select('*').eq('cpa_id', profile.id).order('next_invoice_date')
    setRecurring(r || [])
    setLoading(false)
  }

  function openNew() {
    setEditing(null)
    setForm({ client_id: '', frequency: 'monthly', next_invoice_date: nextDate('monthly'), line_items: [{ description: '', qty: 1, rate: '' }], tax_rate: 0, notes: '', active: true })
    setShowModal(true)
  }

  function openEdit(r) {
    setEditing(r)
    setForm({ client_id: r.client_id, frequency: r.frequency, next_invoice_date: r.next_invoice_date, line_items: r.line_items || [{ description: '', qty: 1, rate: '' }], tax_rate: r.tax_rate || 0, notes: r.notes || '', active: r.active })
    setShowModal(true)
  }

  async function handleSave() {
    if (!form.client_id) { notify('Please select a client'); return }
    const payload = { ...form, cpa_id: profile.id }
    if (editing) {
      await supabase.from('recurring_invoices').update(payload).eq('id', editing.id)
      notify('Recurring invoice updated!')
    } else {
      await supabase.from('recurring_invoices').insert(payload)
      notify('Recurring invoice created!')
    }
    setShowModal(false)
    load()
  }

  async function toggleActive(r) {
    await supabase.from('recurring_invoices').update({ active: !r.active }).eq('id', r.id)
    load()
  }

  async function deleteRecurring(id) {
    if (!window.confirm('Delete this recurring invoice?')) return
    await supabase.from('recurring_invoices').delete().eq('id', id)
    load()
    notify('Deleted.')
  }

  async function generateNow(r) {
    setGenerating(r.id)
    const client = clients.find(c => c.id === r.client_id)
    const subtotal = r.line_items.reduce((s, l) => s + (l.qty * parseFloat(l.rate || 0)), 0)
    const tax = subtotal * ((r.tax_rate || 0) / 100)
    const total = subtotal + tax
    const invoiceNum = `CF-${Date.now().toString().slice(-6)}`

    const { error } = await supabase.from('invoices').insert({
      client_id: r.client_id,
      invoice_number: invoiceNum,
      line_items: r.line_items,
      tax_rate: r.tax_rate,
      total_amount: total,
      status: 'sent',
      notes: r.notes,
      due_date: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0]
    })

    if (!error) {
      // Update next invoice date
      const next = nextDate(r.frequency)
      await supabase.from('recurring_invoices').update({ next_invoice_date: next }).eq('id', r.id)
      notify(`Invoice ${invoiceNum} generated for ${client?.business_name || client?.full_name}!`)
      load()
    }
    setGenerating(null)
  }

  const setLine = (i, k, v) => setForm(f => ({ ...f, line_items: f.line_items.map((l, j) => j === i ? { ...l, [k]: v } : l) }))
  const addLine = () => setForm(f => ({ ...f, line_items: [...f.line_items, { description: '', qty: 1, rate: '' }] }))
  const removeLine = i => setForm(f => ({ ...f, line_items: f.line_items.filter((_, j) => j !== i) }))
  const total = form.line_items.reduce((s, l) => s + (l.qty * parseFloat(l.rate || 0)), 0)

  if (loading) return <div className="skeleton" style={{ height: 200 }} />

  return (
    <div>
      {toast && <div style={{ position: 'fixed', bottom: 24, right: 24, background: '#202326', color: '#CBB17B', padding: '12px 20px', borderRadius: 10, fontSize: 13, fontWeight: 700, zIndex: 9999, boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}>{toast}</div>}

      {/* Header */}
      <div className="card" style={{ padding: '24px 28px', marginBottom: 20, background: '#202326', border: 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ color: '#fff', fontSize: 20, marginBottom: 4 }}>Recurring Invoices</h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, margin: 0 }}>Automate billing for bookkeeping, payroll, and retainer clients.</p>
          </div>
          <button className="btn btn-primary" onClick={openNew}>+ New Recurring Invoice</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 20 }}>
        {[
          { label: 'Active Schedules', value: recurring.filter(r => r.active).length, icon: '🔄' },
          { label: 'Due This Month', value: recurring.filter(r => r.active && new Date(r.next_invoice_date) <= new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)).length, icon: '📅' },
          { label: 'Monthly Recurring', value: `$${recurring.filter(r => r.active && r.frequency === 'monthly').reduce((s, r) => s + r.line_items.reduce((ss, l) => ss + (l.qty * parseFloat(l.rate || 0)), 0), 0).toFixed(2)}`, icon: '💰' },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ fontSize: 28 }}>{s.icon}</div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#202326', fontFamily: "'DM Sans', sans-serif" }}>{s.value}</div>
              <div style={{ fontSize: 12, color: '#62615D' }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* List */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', background: '#F7F3EB', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: '#62615D', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {recurring.length} Recurring Schedule{recurring.length !== 1 ? 's' : ''}
        </div>
        {recurring.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#62615D', fontSize: 13 }}>
            No recurring invoices yet. Create one to automate billing!
          </div>
        ) : recurring.map((r, i) => {
          const client = clients.find(c => c.id === r.client_id)
          const subtotal = r.line_items.reduce((s, l) => s + (l.qty * parseFloat(l.rate || 0)), 0)
          const isDue = new Date(r.next_invoice_date) <= new Date()
          return (
            <div key={r.id} style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14, borderBottom: i < recurring.length - 1 ? '1px solid var(--border)' : 'none', opacity: r.active ? 1 : 0.5 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: FREQ_COLORS[r.frequency] + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>🔄</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#202326' }}>{client?.business_name || client?.full_name || 'Unknown Client'}</div>
                <div style={{ fontSize: 12, color: '#62615D', marginTop: 2 }}>
                  {r.line_items.map(l => l.description).filter(Boolean).join(', ')}
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: FREQ_COLORS[r.frequency] + '20', color: FREQ_COLORS[r.frequency] }}>{FREQ_LABELS[r.frequency]}</span>
              </div>
              <div style={{ textAlign: 'right', minWidth: 100 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#202326' }}>${subtotal.toFixed(2)}</div>
                <div style={{ fontSize: 11, color: isDue ? '#EF4444' : '#62615D', fontWeight: isDue ? 700 : 400 }}>
                  {isDue ? '⚠️ Due now' : `Next: ${new Date(r.next_invoice_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button onClick={() => generateNow(r)} disabled={!r.active || generating === r.id} className="btn btn-primary" style={{ fontSize: 11, padding: '6px 12px' }}>
                  {generating === r.id ? '...' : '⚡ Generate'}
                </button>
                <button onClick={() => openEdit(r)} className="btn btn-ghost" style={{ fontSize: 11, padding: '6px 10px' }}>Edit</button>
                <button onClick={() => toggleActive(r)} className="btn btn-ghost" style={{ fontSize: 11, padding: '6px 10px', color: r.active ? '#F59E0B' : '#10B981' }}>
                  {r.active ? 'Pause' : 'Resume'}
                </button>
                <button onClick={() => deleteRecurring(r.id)} className="btn btn-ghost" style={{ fontSize: 11, padding: '6px 10px', color: '#EF4444' }}>Delete</button>
              </div>
            </div>
          )
        })}
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
          onClick={e => { if (e.target === e.currentTarget) setShowModal(false) }}>
          <div className="card animate-fadeUp" style={{ width: '100%', maxWidth: 560, maxHeight: '90vh', overflow: 'auto', padding: 0 }}>
            <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: '#fff', zIndex: 10 }}>
              <h2 style={{ fontSize: 17 }}>{editing ? 'Edit Recurring Invoice' : 'New Recurring Invoice'}</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, color: '#62615D', cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 22 }}>
              <div className="form-group">
                <label className="label">Client *</label>
                <select className="input" value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))}>
                  <option value="">Select client...</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.business_name || c.full_name}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="label">Frequency</label>
                  <select className="input" value={form.frequency} onChange={e => setForm(f => ({ ...f, frequency: e.target.value, next_invoice_date: nextDate(e.target.value) }))}>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="annually">Annually</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="label">First Invoice Date</label>
                  <input className="input" type="date" value={form.next_invoice_date} onChange={e => setForm(f => ({ ...f, next_invoice_date: e.target.value }))} />
                </div>
              </div>

              <label className="label" style={{ marginBottom: 8, display: 'block' }}>Line Items</label>
              {form.line_items.map((line, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                  <input className="input" style={{ flex: 3 }} placeholder="Description" value={line.description} onChange={e => setLine(i, 'description', e.target.value)} />
                  <input className="input" style={{ width: 60 }} type="number" placeholder="Qty" value={line.qty} onChange={e => setLine(i, 'qty', e.target.value)} min="1" />
                  <input className="input" style={{ width: 90 }} type="number" placeholder="Rate" value={line.rate} onChange={e => setLine(i, 'rate', e.target.value)} />
                  {form.line_items.length > 1 && <button onClick={() => removeLine(i)} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 18 }}>×</button>}
                </div>
              ))}
              <button className="btn btn-ghost" style={{ fontSize: 12, marginBottom: 14 }} onClick={addLine}>+ Add Line</button>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#F7F3EB', padding: '10px 14px', borderRadius: 8, marginBottom: 14 }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>Total per Invoice</span>
                <span style={{ fontSize: 16, fontWeight: 800, color: '#202326' }}>${total.toFixed(2)}</span>
              </div>

              <div className="form-group">
                <label className="label">Notes</label>
                <textarea className="input" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional notes..." />
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button className="btn btn-ghost" onClick={() => setShowModal(false)} style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSave} style={{ flex: 2, justifyContent: 'center' }}>
                  {editing ? 'Save Changes' : 'Create Recurring Invoice'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
